import { lumi } from '@/lib/lumi'

export const CIDADAO_SESSION_KEY = 'geoa_cidadao_session'

export interface CidadaoSession {
  cpf: string
  nomeCompleto: string
}

/** Normaliza CPF: apenas dígitos */
export function normalizarCPF(cpf: string): string {
  return cpf.replace(/\D/g, '')
}

/** Extrai os 6 primeiros dígitos numéricos do CPF como senha padrão */
export function senhaDosCPF(cpf: string): string {
  return normalizarCPF(cpf).slice(0, 6)
}

/**
 * Garante que existe conta cidadão para o CPF.
 * Cria apenas se não existir (1 CPF = 1 conta permanente).
 */
export async function garantirContaCidadao(cpf: string, nomeCompleto: string): Promise<void> {
  const cpfLimpo = normalizarCPF(cpf)

  let jaExiste = false
  try {
    const { list } = await lumi.entities.cidadaos_portal.list({
      filter: { cpf: cpfLimpo },
      limit: 1,
    })
    jaExiste = !!(list && list.length > 0)
  } catch (listErr: unknown) {
    const msg = listErr instanceof Error ? listErr.message : String(listErr)
    console.error(`[GEOA] garantirContaCidadao → erro ao verificar existência (CPF: ${cpfLimpo}):`, msg, listErr)
    throw new Error(`Falha ao verificar conta do Portal do Cidadão: ${msg}`)
  }

  if (jaExiste) {
    console.info(`[GEOA] garantirContaCidadao → conta já existe para CPF: ${cpfLimpo} — nenhuma ação necessária.`)
    return
  }

  try {
    await lumi.entities.cidadaos_portal.create({
      cpf: cpfLimpo,
      nomeCompleto,
      ativo: true,
      creator: 'sistema',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
    console.info(`[GEOA] garantirContaCidadao → conta criada com sucesso para CPF: ${cpfLimpo}`)
  } catch (createErr: unknown) {
    const msg = createErr instanceof Error ? createErr.message : String(createErr)
    console.error(`[GEOA] garantirContaCidadao → erro ao criar conta (CPF: ${cpfLimpo}):`, msg, createErr)
    throw new Error(`Falha ao criar conta do Portal do Cidadão: ${msg}`)
  }
}

/**
 * Autentica o cidadão pelo CPF e senha.
 * A senha pode ser a padrão (6 primeiros dígitos do CPF) ou uma senha
 * personalizada salva em `cidadaos_portal.senhaCustom`.
 */
export async function loginCidadao(
  cpf: string,
  senha: string,
): Promise<CidadaoSession | null> {
  const cpfLimpo = normalizarCPF(cpf)

  try {
    const { list } = await lumi.entities.cidadaos_portal.list({
      filter: { cpf: cpfLimpo },
      limit: 1,
    })
    const conta = list?.[0] as { nomeCompleto?: string; ativo?: boolean; senhaCustom?: string; _id?: string } | undefined
    if (!conta || conta.ativo === false) return null

    // Senha válida: customizada (se existir) OU padrão (6 primeiros dígitos do CPF)
    const senhaValida = conta.senhaCustom ? conta.senhaCustom : senhaDosCPF(cpfLimpo)
    if (senha !== senhaValida) return null

    return {
      cpf: cpfLimpo,
      nomeCompleto: conta.nomeCompleto || cpfLimpo,
    }
  } catch {
    return null
  }
}

/**
 * Altera a senha do cidadão.
 * Atualiza em `cidadaos_portal` (senhaCustom) e em `usuarios_sistema` (senhaAtual).
 * Retorna a nova senha salva.
 */
export async function alterarSenhaCidadao(cpf: string, novaSenha: string): Promise<void> {
  const cpfLimpo = normalizarCPF(cpf)
  const agora = new Date().toISOString()

  // 1. Busca o _id em cidadaos_portal
  const { list: listPortal } = await lumi.entities.cidadaos_portal.list({
    filter: { cpf: cpfLimpo },
    limit: 1,
  })
  const contaPortal = listPortal?.[0] as { _id?: string } | undefined
  if (!contaPortal?._id) throw new Error('Conta não encontrada no Portal do Cidadão.')

  // 2. Atualiza cidadaos_portal
  await lumi.entities.cidadaos_portal.update(contaPortal._id, {
    senhaCustom: novaSenha,
    updatedAt: agora,
  })

  // 3. Atualiza usuarios_sistema (campo senhaAtual para admin ver)
  try {
    const { list: listUsers } = await lumi.entities.usuarios_sistema.list({
      filter: { email: `${cpfLimpo}@cidadao.geoa` },
      limit: 1,
    })
    const userSistema = listUsers?.[0] as { _id?: string } | undefined
    if (userSistema?._id) {
      await lumi.entities.usuarios_sistema.update(userSistema._id, {
        senha: novaSenha,      // mantém campo obrigatório sincronizado
        senhaAtual: novaSenha, // campo visível para o admin em Usuários
        updatedAt: agora,
      })
      console.info(`[GEOA] alterarSenhaCidadao → usuarios_sistema atualizado para CPF: ${cpfLimpo}`)
    } else {
      console.warn(`[GEOA] alterarSenhaCidadao → registro em usuarios_sistema não encontrado para CPF: ${cpfLimpo}`)
    }
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err)
    console.error(`[GEOA] alterarSenhaCidadao → falha ao atualizar usuarios_sistema (CPF: ${cpfLimpo}):`, msg)
    // Não re-lança — a alteração principal (cidadaos_portal) já foi concluída
  }
}

/**
 * Alias idempotente para garantirContaCidadao — usado em SolicitacaoPublicaPage.
 */
export async function criarOuObterCidadao(cpf: string, nomeCompleto: string): Promise<void> {
  return garantirContaCidadao(cpf, nomeCompleto)
}

export function salvarSessaoCidadao(s: CidadaoSession): void {
  sessionStorage.setItem(CIDADAO_SESSION_KEY, JSON.stringify(s))
}

export function lerSessaoCidadao(): CidadaoSession | null {
  try {
    const raw = sessionStorage.getItem(CIDADAO_SESSION_KEY)
    return raw ? (JSON.parse(raw) as CidadaoSession) : null
  } catch {
    return null
  }
}

export function limparSessaoCidadao(): void {
  sessionStorage.removeItem(CIDADAO_SESSION_KEY)
}
